import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

const normalizePrivileges = (value) => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((privilege) => privilege.trim())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  return [];
};

const resolvePrivileges = (...privilegeGroups) =>
  privilegeGroups
    .map((privilegeGroup) => normalizePrivileges(privilegeGroup))
    .find((privileges) => privileges.length > 0) || [];

export function useSubscription() {
  const subscriptionPrivileges = useSelector(
    (state) => state?.privilegesReducer?.subscriptionPrivileges,
  );
  const rootSubscriptionPrivileges = useSelector(
    (state) => state?.privilegesReducer?.rootSubscriptionPrivileges,
  );
  const authPrivileges = useSelector(
    (state) => state?.authReducer?.userData?.product_Privileges,
  );

  const privileges = useMemo(
    () =>
      resolvePrivileges(
        subscriptionPrivileges,
        rootSubscriptionPrivileges,
        authPrivileges,
      ),
    [subscriptionPrivileges, rootSubscriptionPrivileges, authPrivileges],
  );

  const hasSubscription = useCallback(
    (permission, privilegesList) => {
      if (!permission) {
        return false;
      }

      const requiredPrivileges = Array.isArray(permission)
        ? permission
        : [permission];
      const availablePrivileges = normalizePrivileges(privilegesList ?? privileges);

      return requiredPrivileges.every((privilege) =>
        availablePrivileges.includes(privilege),
      );
    },
    [privileges],
  );

  return { hasSubscription, privileges };
}
